import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { finalize } from 'rxjs/operators'
import * as orgAction from '../../actions/organization'
import { HubtypeBot } from '../../models/hubtype-bot'
import { HubtypeBotConfig } from '../../models/hubtype-bot-config'
import { HubtypeOrganization } from '../../models/hubtype-organization'
import {
  HubtypeProviderAccount,
  filterEnabledProviders,
} from '../../models/hubtype-provider-account'
import { HubtypeQueue } from '../../models/hubtype-queue'
import * as fromRoot from '../../reducers'
import { BotService } from '../../services/hubtype-api/bot.service'
import { ProviderAccountService } from '../../services/hubtype-api/provider-account.service'
import { AngularComponent } from '../AngularComponent'

@Component({
  selector: 'app-add-channels',
  templateUrl: './add-channels.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./add-channels.component.scss'],
  standalone: false,
})
export class AddChannelsComponent extends AngularComponent implements OnInit {
  public selectedProvider
  public providerAccount: HubtypeProviderAccount
  public step = 1
  private organization: HubtypeOrganization
  public bot: HubtypeBot
  public queue: HubtypeQueue
  public feedback = ''
  public botConfig: HubtypeBotConfig | null = null

  get selectMessengerText(): string {
    return this.translate.instant('shared.addChannels.selectMessenger')
  }
  get selectAnotherMessengerText(): string {
    return this.translate.instant('shared.addChannels.selectAnotherMessenger')
  }
  get genericChannelTitle(): string {
    return this.translate.instant('shared.addChannels.generic.title')
  }
  get genericChannelContact(): string {
    return this.translate.instant('shared.addChannels.generic.contactUs')
  }
  get connectingText(): string {
    return this.translate.instant('shared.addChannels.connecting', {
      name: this.providerAccount?.name ?? '',
    })
  }
  get successTitle(): string {
    return this.translate.instant('shared.addChannels.successTitle')
  }
  get integrationReadyText(): string {
    return this.translate.instant('shared.addChannels.integrationReady')
  }
  get connectedPrefixText(): string {
    return this.translate.instant('shared.addChannels.connectedPrefix', {
      provider: this.providerAccount?.provider ?? '',
      name: this.providerAccount?.name ?? '',
    })
  }
  get connectedQueueText(): string {
    return this.translate.instant('shared.addChannels.theQueue', {
      name: this.queue?.name ?? '',
    })
  }
  get connectedBotText(): string {
    return this.translate.instant('shared.addChannels.theBot', {
      name: this.bot?.name ?? '',
    })
  }
  get webchatInstructionsText(): string {
    return this.translate.instant('shared.addChannels.webchatInstructions')
  }
  get moreInfoDocsText(): string {
    return this.translate.instant('shared.addChannels.moreInfoDocs')
  }
  get botonicDocsText(): string {
    return this.translate.instant('shared.addChannels.botonicDocs')
  }
  get testBotNumberText(): string {
    return this.translate.instant('shared.addChannels.testBotNumber')
  }
  get addAnotherMessengerText(): string {
    return this.translate.instant('shared.addChannels.addAnotherMessenger')
  }
  get finishText(): string {
    return this.translate.instant('shared.addChannels.finish')
  }
  get errorTitleText(): string {
    return this.translate.instant('shared.addChannels.errorTitle')
  }
  get tryAgainText(): string {
    return this.translate.instant('shared.addChannels.tryAgain')
  }
  get problemConnectingText(): string {
    return this.translate.instant('shared.addChannels.problemConnecting', {
      name: this.providerAccount?.name ?? '',
      feedback: this.feedback,
    })
  }

  @Output() newChannel = new EventEmitter<HubtypeProviderAccount>()
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
    private ref: ChangeDetectorRef,
    private providerAccountService: ProviderAccountService,
    private botService: BotService,
    private translate: TranslateService
  ) {
    super()
  }

  ngOnInit() {
    this.subscribeUntilDestroy(
      this.store.select(fromRoot.getOrganization),
      org => {
        this.ref.markForCheck()
        this.organization = org
      }
    )
    this.subscribeUntilDestroy(this.store.select(fromRoot.getBot), b => {
      this.ref.markForCheck()
      this.bot = b
      if (b?.id) {
        this.loadBotConfig(b.id)
      }
    })
    this.subscribeUntilDestroy(
      this.store.select(fromRoot.getSelectedQueue),
      q => {
        this.ref.markForCheck()
        this.queue = q
      }
    )
    this.subscribeUntilDestroy(this.route.paramMap, (params: ParamMap) => {
      const provider = params.get('provider')
      if (this.providers.includes(provider)) {
        this.selectProvider(provider)
      }
    })
  }

  private loadBotConfig(botId: string) {
    this.subscribeUntilDestroy(
      this.botService
        .getBotConfig(botId)
        .pipe(finalize(() => this.ref.markForCheck())),
      config => {
        this.botConfig = config
      },
      () => {
        // If bot config fails to load, continue without it
        this.botConfig = null
      }
    )
  }

  init() {
    this.ref.markForCheck()
    this.step = 1
    this.selectedProvider = null
    this.providerAccount = null
  }

  get providers(): string[] {
    if (this.bot) {
      return filterEnabledProviders(
        HubtypeProviderAccount.BOT_PROVIDERS,
        this.organization
      )
    }
    if (this.queue) {
      return filterEnabledProviders(
        HubtypeProviderAccount.QUEUE_PROVIDERS,
        this.organization
      )
    }
    return []
  }

  getProviderName(p) {
    return HubtypeProviderAccount.names[p]
  }

  selectProvider(p) {
    this.ref.markForCheck()
    this.selectedProvider = p
    this.step = 2
  }

  providerCreated(provider?: HubtypeProviderAccount) {
    this.ref.markForCheck()
    if (!provider) {
      return
    }
    if (this.bot) {
      provider.bot_id = this.bot.id
    }
    if (this.queue) {
      provider.queue_id = this.queue.id
    }
    this.providerAccount = provider
    const providerAccount = this.providerAccountService
      .connectProvider(provider)
      .pipe(finalize(() => this.ref.markForCheck()))

    this.subscribeUntilDestroy(
      providerAccount,
      pa => {
        this.providerAccount = pa
        this.store.dispatch(
          new orgAction.AddProviderAccountAction(this.providerAccount)
        )
        this.step = 4
      },
      err => {
        this.feedback = err.detail
          ? err.detail
          : this.translate.instant('shared.addChannels.unexpectedError')
        this.step = 5
      }
    )
    this.step = 3
  }

  back() {
    this.backRoute(this.router, this.route)
  }
}
